import React, { useState, useEffect } from 'react';
import { Bell } from 'lucide-react';
import notificationService from '../../services/notificationService';
import toast from 'react-hot-toast';

/**
 * Contextual notification toggle for study/site-specific subscriptions
 * Automatically scopes subscriptions to the current context (study, site, participant)
 */
const ContextualNotificationToggle = ({ 
  eventKey,
  eventTypeId = null,
  label, 
  description,
  studyId = null,
  siteId = null,
  participantId = null,
  className = ''
}) => {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [subscriptionId, setSubscriptionId] = useState(null);

  useEffect(() => {
    checkSubscription();
  }, [eventKey, eventTypeId, studyId, siteId, participantId]);

  const checkSubscription = async () => {
    try {
      setLoading(true);
      
      // Fetch user's subscriptions
      const subscriptions = await notificationService.getSubscriptions();
      
      // Find if user has a subscription for this event type with matching scope
      const existingSubscription = subscriptions.find(sub => {
        const eventMatches = eventTypeId 
          ? sub.event_type === eventTypeId
          : sub.event_type_key === eventKey;
        
        // Convert to strings for comparison to handle type mismatches (URL params are strings, API returns integers)
        const studyMatches = studyId ? String(sub.study) === String(studyId) : !sub.study;
        const siteMatches = siteId ? String(sub.site) === String(siteId) : !sub.site;
        const participantMatches = participantId ? String(sub.participant_id) === String(participantId) : !sub.participant_id;
        
        return eventMatches && studyMatches && siteMatches && participantMatches;
      });

      if (existingSubscription) {
        setEnabled(existingSubscription.enabled && existingSubscription.in_app_enabled);
        setSubscriptionId(existingSubscription.id);
      } else {
        setEnabled(false);
        setSubscriptionId(null);
      }
    } catch (error) {
      console.error('Failed to check subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSubscription = async () => {
    setLoading(true);
    try {
      if (enabled && subscriptionId) {
        // Disable existing subscription
        await notificationService.updateSubscription(subscriptionId, {
          enabled: false,
          in_app_enabled: false
        });
        setEnabled(false);
        toast.success(`Unsubscribed from "${label}" notifications`);
      } else if (subscriptionId) {
        // Re-enable existing subscription
        await notificationService.updateSubscription(subscriptionId, {
          enabled: true,
          in_app_enabled: true
        });
        setEnabled(true);
        toast.success(`Subscribed to "${label}" notifications for this study`);
      } else {
        // Create new subscription
        // First, get event type ID if we don't have it
        let finalEventTypeId = eventTypeId;
        if (!finalEventTypeId && eventKey) {
          const eventTypes = await notificationService.getEventTypes();
          const eventType = eventTypes.find(et => et.key === eventKey);
          if (!eventType) {
            throw new Error(`Event type not found: ${eventKey}`);
          }
          finalEventTypeId = eventType.id;
        }

        const newSubscription = await notificationService.createSubscription({
          event_type: finalEventTypeId,
          study: studyId ? parseInt(studyId, 10) : null,
          site: siteId ? parseInt(siteId, 10) : null,
          participant_id: participantId || null,
          in_app_enabled: true,
          email_enabled: false, // Default to in-app only for contextual subscriptions
          enabled: true
        });
        
        setSubscriptionId(newSubscription.id);
        setEnabled(true);
        toast.success(`Subscribed to "${label}" notifications for this study`);
      }
    } catch (error) {
      console.error('Failed to toggle subscription:', error);
      toast.error('Failed to update notification preference');
      // Revert state on error
      setEnabled(!enabled);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`flex items-center justify-between p-3 border rounded-lg hover:border-primary-300 transition-colors ${className}`}>
      <div className="flex items-start gap-3 flex-1">
        <Bell className={`h-5 w-5 mt-0.5 ${enabled ? 'text-primary-600' : 'text-gray-400'}`} />
        <div className="flex-1 min-w-0">
          <div className="font-medium text-gray-900 dark:text-gray-100">{label}</div>
          <div className="text-sm text-gray-500 mt-0.5">{description}</div>
        </div>
      </div>
      
      <button
        onClick={toggleSubscription}
        disabled={loading}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
          enabled ? 'bg-primary-600' : 'bg-gray-200'
        } ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            enabled ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );
};

export default ContextualNotificationToggle;
